"use client";

import React from "react";
import { ArrowRight, MapPin, PoundSterling, CalendarClock, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
export default function QuickLinks() {
  const shouldReduceMotion = useReducedMotion();
  const services = [{
    title: "Local SEO",
    to: "/LocalSEOPage",
    tag: "Targeted Growth",
    description: "Rank in regional maps and local search queries where nearby customers are actively looking for your expertise.",
    icon: MapPin
  }, {
    title: "Affordable SEO Services UK",
    to: "/AffordableSeoServicesUkPage",
    tag: "High Value",
    description: "Direct consultant support without agency overhead, focused on clear deliverables and measurable organic gains.",
    icon: PoundSterling
  }, {
    title: "Monthly SEO Services",
    to: "/MonthlySeoServicesPage",
    tag: "Ongoing Strategy",
    description: "Consistent technical audits, content optimisation, and proactive keyword roadmaps adapted month by month.",
    icon: CalendarClock
  }];
  return <section id="featuredservicesquicklinks" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-3">
              <Badge variant="secondary" className="font-normal px-3 py-1">
                <Sparkles className="size-3 text-accent" />
                <span>Specialised SEO Tracks</span>
              </Badge>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground">
              Featured SEO advisory pathways
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Explore specialised search programmes tailored to your exact market scope. When you work with me, you get strategic clarity and direct consultant execution.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-sm">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.title} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.45,
            delay: index * 0.1,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full border border-border/80 bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 group flex flex-col justify-between">
                  <CardContent className="p-6 sm:p-7 flex flex-col h-full justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-2.5 rounded-lg bg-secondary text-primary">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                          {service.tag}
                        </span>
                      </div>

                      <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        {service.title}
                      </h3>

                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    <div className="pt-6 mt-6 border-t border-border/60">
                      <Link to={service.to} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link">
                        <span>Explore service details</span>
                        <ArrowRight className="size-3.5 transition-transform duration-200 group-hover/link:translate-x-1" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.35
      }} className="mt-12 rounded-xl bg-secondary/50 border border-border p-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h4 className="text-base font-semibold text-foreground">
              Looking for full-scope search consulting?
            </h4>
            <p className="text-sm text-muted-foreground mt-0.5">
              I also handle technical architecture, international setups, and AI search visibility.
            </p>
          </div>
          <Button asChild variant="outline" size="sm" className="shrink-0">
            <Link to="/ContactPage">
              Discuss your project
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}